import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { createClientComponentClient } from '@/integrations/supabase/client';

interface AdminUser {
  id: string;
  user_id: string;
  email: string;
  role: 'super_admin' | 'admin' | 'moderator';
  created_at: string;
  updated_at: string;
  is_active: boolean;
}

// Global state to prevent multiple simultaneous requests across components
let globalAdminPromise: Promise<AdminUser | null> | null = null;
let globalLastUserId: string | null = null;
let globalLastFetchAt: number = 0;
let globalAdminUser: AdminUser | null = null;

export const useAdmin = () => {
  const { user } = useAuth();
  const supabase = createClientComponentClient();
  const [adminUser, setAdminUser] = useState<AdminUser | null>(globalAdminUser);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkAdminStatus = useCallback(async () => {
    if (!user) {
      setAdminUser(null);
      setLoading(false);
      return;
    }

    // Check cache first for instant recognition
    const cachedAdminStatus = localStorage.getItem(`admin_${user.id}`)
    if (cachedAdminStatus && Date.now() - globalLastFetchAt < 30000) {
      try {
        const cached = JSON.parse(cachedAdminStatus)
        setAdminUser(cached)
        setLoading(false)
        console.log('[ADMIN] Using cached admin status')
        return
      } catch {
        localStorage.removeItem(`admin_${user.id}`)
      }
    }

    setLoading(true);
    setError(null);

    // If a request is already in-flight, await its result instead of instantly returning true and freezing states
    if (globalAdminPromise) {
      try {
        const result = await globalAdminPromise;
        // Verify user hasn't changed while waiting
        if (globalLastUserId === user.id || !globalLastUserId) {
          setAdminUser(result);
        }
      } catch (err) {
        console.error('Error in parallel admin wait:', err);
      } finally {
        setLoading(false);
      }
      return;
    }

    console.log('[ADMIN] Checking admin status for user:', user.id);
    const perfStart = performance.now();

    // Create the shared promise that other components can hook into
    globalAdminPromise = (async () => {
      const abortController = new AbortController();
      const timeoutId = setTimeout(() => abortController.abort(), 5000); // 5s timeout

      try {
        // We do NOT need to await supabase.auth.getSession() here since
        // supabase.functions.invoke will automatically handle session tokens
        const adminCheckData = await Promise.race([
          supabase.functions.invoke('admin', {
            body: { action: 'check_admin_status', user_id: user.id }
          }),
          new Promise<never>((_, reject) => {
            const err = new Error('Admin check timed out');
            err.name = 'AbortError';
            setTimeout(() => reject(err), 5000);
          })
        ]);
        const { data, error: adminError } = adminCheckData as { data: any, error: any };

        clearTimeout(timeoutId);

        const perfDuration = performance.now() - perfStart;
        console.log(`[ADMIN PERF] Admin check completed in ${perfDuration.toFixed(0)}ms`);

        if (adminError) {
          const status = (adminError as unknown as { status?: number }).status;
          if (status === 403 || status === 404 || status === 401) {
            console.log('[ADMIN] Function access denied or not found, user is not admin');
            return null;
          } else if (adminError.message?.toLowerCase().includes('forbidden')) {
            return null;
          } else if (adminError.message?.includes('not found') || adminError.message?.includes('PGRST116')) {
            return null;
          } else {
            console.warn('[ADMIN] Error checking status:', adminError.message);
            return null; // Default to not admin on error
          }
        } else if (data?.success && data?.data) {
          console.log('[ADMIN] User is admin:', data.data);
          return data.data as AdminUser;
        } else {
          console.log('[ADMIN] User is not admin');
          return null;
        }
      } catch (err: unknown) {
        clearTimeout(timeoutId);
        if (err instanceof Error && err.name === 'AbortError') {
          console.warn('[ADMIN] Admin check timed out');
        }
        return null;
      }
    })();

    try {
      const result = await globalAdminPromise;
      if (result) {
        globalAdminUser = result;
        localStorage.setItem(`admin_${user.id}`, JSON.stringify(result));
      } else {
        globalAdminUser = null;
        localStorage.removeItem(`admin_${user.id}`);
      }
      setAdminUser(result);

      // Cache the last fetch info
      globalLastUserId = user.id;
      globalLastFetchAt = Date.now();
    } catch (err) {
      console.error('Error checking admin status:', err);
      setAdminUser(null);
    } finally {
      globalAdminPromise = null;
      setLoading(false);
    }
  }, [user?.id, supabase]);

  useEffect(() => {
    if (user?.id) {
      const now = Date.now();
      // Only throttle if we already have admin data and it's the same user within 15s
      if (
        globalAdminUser !== null &&
        globalLastUserId === user.id &&
        now - globalLastFetchAt < 15000
      ) {
        setAdminUser(globalAdminUser);
        setLoading(false);
        return;
      }

      // Reset throttling for new users
      if (globalLastUserId !== user.id) {
        globalLastUserId = user.id;
        globalLastFetchAt = 0;
        setAdminUser(null); // Clear old admin data
        globalAdminUser = null;
      }
      checkAdminStatus();
    } else {
      setAdminUser(null);
      setLoading(false);
      globalLastUserId = null;
      globalLastFetchAt = 0;
      globalAdminUser = null;
    }
  }, [user?.id, checkAdminStatus]);

  // Also listen to auth state changes indirectly by forcing a refetch
  // when local storage session changes (another tab, manual sign out, etc.)
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'sb-auth-token') {
        // Session changed in another tab – re-evaluate admin state
        setTimeout(() => checkAdminStatus(), 0);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [checkAdminStatus]);

  const isAdmin = adminUser !== null && adminUser.is_active;
  const isSuperAdmin = adminUser?.role === 'super_admin';
  const isModerator = adminUser?.role === 'moderator';

  // --- Education Role Helpers ---
  // Derived from userProfile (no extra DB call — already loaded by AuthContext)
  const { userProfile } = useAuth();
  const facilityRole = userProfile?.facility_role ?? null;
  const hasFacility = !!userProfile?.facility_id;

  /** School Principal OR Family Education Admin (facility_role = 'admin' in an edu hub) */
  const isEducationAdmin = hasFacility && facilityRole === 'admin' && !isAdmin;

  /** Teacher assigned to a school hub */
  const isTeacher = hasFacility && facilityRole === 'teacher';

  /** School Principal specifically (admin of a school-type hub)
   *  The distinction from family edu admin is resolved in EducationAdminPage
   *  by checking facilities.type — we keep this flag for quick guard checks. */
  const isSchoolPrincipal = isEducationAdmin; // refined per-page if needed

  /** Parent / Guardian managing home learners (admin of a family_edu hub) */
  const isParentAdmin = isEducationAdmin; // refined per-page if needed

  /** Student / home learner (member of an edu hub OR solo learner with no facility) */
  const isStudentUser = facilityRole === 'member' || (!hasFacility && !isAdmin && !isTeacher && !isEducationAdmin);

  const hasPermission = (permission: string): boolean => {
    if (!adminUser || !adminUser.is_active) return false;
    if (isSuperAdmin) return true;

    // For now, all admins have all permissions
    // TODO: Implement permission-based access control
    console.log(`[ADMIN] Checking permission: ${permission} for user: ${adminUser.role}`);
    return true;
  };

  return {
    adminUser,
    isAdmin,
    isSuperAdmin,
    isModerator,
    hasPermission,
    loading,
    error,
    refetch: checkAdminStatus,
    // Education role helpers
    isEducationAdmin,
    isTeacher,
    isSchoolPrincipal,
    isParentAdmin,
    isStudentUser,
  };
};